/**
 * Permission Constants
 * Centralized permission definitions matching backend permissions
 * @module constants/PermissionConstant
 */

// Contract Permissions
export const PERMISSIONS = {
  // Contract Management
  CAN_VIEW_CONTRACTS: "can_view_contracts",
  CAN_MANAGE_CONTRACTS: "can_manage_contracts",

  // Contract Documents
  CAN_VIEW_CONTRACT_DOCUMENTS: "can_view_contract_documents",
  CAN_MANAGE_CONTRACT_DOCUMENTS: "can_manage_contract_documents",

  // Contract Roles
  CAN_VIEW_CONTRACT_ROLES: "can_view_contract_roles",
  CAN_ASSIGN_CONTRACT_ROLES: "can_assign_contract_roles",

  // As Signed Contract
  CAN_VIEW_SIGNED_CONTRACT: "can_view_signed_contract",
  CAN_DELETE_SIGNED_CONTRACT: "can_delete_signed_contract",
  CAN_DOWNLOAD_SIGNED_CONTRACT: "can_download_signed_contract",

  // Contract Tasks
  CAN_VIEW_CONTRACT_TASKS: "can_view_contract_tasks",
  CAN_MANAGE_CONTRACT_TASKS: "can_manage_contract_tasks",
  CAN_MANAGE_CONTRACT_TASKS_DOCUMENTS: "can_manage_contract_tasks_documents",

  // Task Management
  CAN_MANAGE_TASK_UPDATES: "can_manage_task_updates",
  CAN_VIEW_TASK_UPDATES: "can_view_task_updates",
  CAN_RESOLVE_TASK_ISSUE: "can_resolve_task_issue",

  // Contract Obligations
  CAN_VIEW_CONTRACT_OBLIGATIONS: "can_view_contract_obligations",
  CAN_MANAGE_CONTRACT_OBLIGATIONS: "can_manage_contract_obligations",

  // Contract Financials
  CAN_VIEW_CONTRACT_FINANCIALS: "can_view_contract_financials",
  CAN_MANAGE_CONTRACT_FINANCIALS: "can_manage_contract_financials",
  CAN_MANAGE_PAYMENTS_RECEIVED: "can_manage_payments_received",

  // Negative Covenants
  CAN_VIEW_NEGATIVE_COVENANTS: "can_view_negative_covenants",
  CAN_MANAGE_NEGATIVE_COVENANTS: "can_manage_negative_covenants",

  // Timeline Extensions
  CAN_VIEW_TIMELINE_EXTENSION_REQUESTS: "can_view_timeline_extension_requests",
  CAN_REQUEST_TIMELINE_EXTENSION: "can_request_timeline_extension",
  CAN_APPROVE_TIMELINE_EXTENSION: "can_approve_timeline_extension",

  // Schedule Extensions
  CAN_VIEW_SCHEDULE_EXTENSIONS: "can_view_schedule_extensions",
  CAN_MANAGE_SCHEDULE_EXTENSIONS: "can_manage_schedule_extensions",

  //SUSPENSION Event
  CAN_VIEW_SUSPENSION_EVENT: "can_view_suspension_events",
  CAN_MANAGE_SUSPENSION_EVENT: "can_manage_suspension_events",

  //Event Update
  CAN_MANAGE_EVENT_UPDATE: "can_manage_event_update",

  // User Management
  CAN_VIEW_USERS: "can_view_users",
  CAN_MANAGE_USERS: "can_manage_users",
  CAN_MANAGE_LAW_USERS: "can_manage_law_users",

  // Organisation Management
  CAN_VIEW_ORGANISATIONS: "can_view_organisations",
  CAN_MANAGE_ORGANISATIONS: "can_manage_organisations",

  // Role Management
  CAN_VIEW_ROLE_MANAGEMENT: "can_view_role_management",
  CAN_MANAGE_ROLE_MANAGEMENT: "can_manage_role_management",

  // ROLE MANAGEMENT
  CAN_VIEW_ROLES: "can_view_roles",
  CAN_MANAGE_ROLES: "can_manage_roles",

  // Entity Management
  CAN_VIEW_ENTITIES: "can_view_entities",

  // Dashboard
  CAN_VIEW_DASHBOARD: "can_view_dashboard",

  // Profile
  CAN_EDIT_PROFILE: "can_edit_profile",
};

// Contract-Level-Permission-keys
export const CONTRACT_PERMISSIONS = {
  // As Signed Contract
  CAN_VIEW_SIGNED_CONTRACT: "can_view_signed_contract",
  CAN_DELETE_SIGNED_CONTRACT: "can_delete_signed_contract",
  CAN_DOWNLOAD_SIGNED_CONTRACT: "can_download_signed_contract",

  // Repository
  CAN_VIEW_REPOSITORY: "can_view_repository",
  CAN_CREATE_REPOSITORY: "can_create_repository",
  CAN_DELETE_REPOSITORY: "can_delete_repository",
  CAN_DOWNLOAD_REPOSITORY: "can_download_repository",

  // Responsibility Assignments
  CAN_VIEW_RESPONSIBILITY: "can_view_responsibility_assignments",

  // Obligations
  CAN_VIEW_OBLIGATIONS: "can_view_contract_obligations",
  CAN_MANAGE_OBLIGATIONS: "can_manage_contract_obligations",

  // Project Schedule
  CAN_VIEW_PROJECT_SCHEDULE: "can_view_contract_tasks",
  CAN_MANAGE_PROJECT_SCHEDULE: "can_manage_contract_tasks",

  // Finance
  CAN_VIEW_FINANCE: "can_view_contract_financials",
  CAN_MANAGE_PAYMENT_FINANCE: "can_manage_payment_contract_financials",

  // Approvals
  CAN_VIEW_APPROVALS: "can_view_approvals",
  CAN_MANAGE_APPROVALS: "can_manage_approvals",
  CAN_CREATE_APPROVALS: "can_create_approvals",
  CAN_DELETE_APPROVALS: "can_delete_approvals",
  CAN_UPDATE_APPROVALS: "can_edit_approvals",

  // Negative Covenants
  CAN_VIEW_NEGATIVE_COVENANTS: "can_view_negative_covenants",

  // Suspension Events
  CAN_VIEW_SUSPENSION_EVENTS: "can_view_suspension_events",
  CAN_CREATE_SUSPENSION_EVENTS: "can_create_suspension_events",
  CAN_UPDATE_SUSPENSION_EVENTS: "can_update_suspension_events",
  CAN_DELETE_SUSPENSION_EVENTS: "can_delete_suspension_events",
  CAN_DOWNLOAD_SUSPENSION_EVENTS: "can_download_suspension_event",
  CAN_MANAGE_SUSPENSION_EVENT_UPDATE: "can_manage_suspension_events_updates",

  // Project Timeline
  CAN_VIEW_PROJECT_TIMELINE: "can_view_project_timeline",
  CAN_CREATE_PROJECT_TIMELINE: "can_create_project_timeline",
  CAN_EDIT_PROJECT_TIMELINE: "can_edit_project_timeline",
  CAN_DELETE_PROJECT_TIMELINE: "can_delete_project_timeline",

  // Reporting / Meeting
  CAN_VIEW_REPORTING_MEETING: "can_view_reporting_meeting",
  CAN_CREATE_REPORTING_MEETING: "can_create_reporting_meeting",
  CAN_EDIT_REPORTING_MEETING: "can_edit_reporting_meeting",
  CAN_DELETE_REPORTING_MEETING: "can_delete_reporting_meeting",

  // Deviations
  CAN_VIEW_DEVIATIONS: "can_view_deviations",
  CAN_CREATE_DEVIATIONS: "can_create_deviations",
  CAN_EDIT_DEVIATIONS: "can_edit_deviations",
  CAN_DELETE_DEVIATIONS: "can_delete_deviations",
};

/**
 * Permission groups for common use cases
 * Use these to check multiple related permissions at once
 */
export const PERMISSION_GROUPS = {
  CONTRACT_FULL_ACCESS: [
    PERMISSIONS.CAN_VIEW_CONTRACTS,
    PERMISSIONS.CAN_MANAGE_CONTRACTS,
  ],

  CONTRACT_READ_ONLY: [PERMISSIONS.CAN_VIEW_CONTRACTS],

  USER_MANAGEMENT: [PERMISSIONS.CAN_VIEW_USERS, PERMISSIONS.CAN_MANAGE_USERS],

  ORGANISATION_MANAGEMENT: [
    PERMISSIONS.CAN_VIEW_ORGANISATIONS,
    PERMISSIONS.CAN_MANAGE_ORGANISATIONS,
  ],

  TASK_MANAGEMENT: [
    PERMISSIONS.CAN_VIEW_CONTRACT_TASKS,
    PERMISSIONS.CAN_MANAGE_CONTRACT_TASKS,
  ],
};

/**
 * Action-based permission mapping
 * Maps UI actions to required permissions
 */
export const ACTION_PERMISSIONS = {
  // Contract Actions
  CREATE_CONTRACT: PERMISSIONS.CAN_MANAGE_CONTRACTS,
  EDIT_CONTRACT: PERMISSIONS.CAN_MANAGE_CONTRACTS,
  DELETE_CONTRACT: PERMISSIONS.CAN_MANAGE_CONTRACTS,
  VIEW_CONTRACT: PERMISSIONS.CAN_VIEW_CONTRACTS,

  // User Actions
  CREATE_USER: PERMISSIONS.CAN_MANAGE_USERS,
  EDIT_USER: PERMISSIONS.CAN_MANAGE_USERS,
  DELETE_USER: PERMISSIONS.CAN_MANAGE_USERS,
  VIEW_USERS: PERMISSIONS.CAN_VIEW_USERS,

  // Law Firm User Actions
  CREATE_LAW_USER: PERMISSIONS.CAN_MANAGE_LAW_USERS,
  EDIT_LAW_USER: PERMISSIONS.CAN_MANAGE_LAW_USERS,
  DELETE_LAW_USER: PERMISSIONS.CAN_MANAGE_LAW_USERS,
  VIEW_LAW_USERS: PERMISSIONS.CAN_MANAGE_LAW_USERS,

  // Organisation Actions
  CREATE_ORGANISATION: PERMISSIONS.CAN_MANAGE_ORGANISATIONS,
  EDIT_ORGANISATION: PERMISSIONS.CAN_MANAGE_ORGANISATIONS,
  DELETE_ORGANISATION: PERMISSIONS.CAN_MANAGE_ORGANISATIONS,
  VIEW_ORGANISATIONS: PERMISSIONS.CAN_VIEW_ORGANISATIONS,

  // Task Actions
  CREATE_TASK: PERMISSIONS.CAN_MANAGE_CONTRACT_TASKS,
  EDIT_TASK: PERMISSIONS.CAN_MANAGE_CONTRACT_TASKS,
  DELETE_TASK: PERMISSIONS.CAN_MANAGE_CONTRACT_TASKS,
  RESOLVE_TASK: PERMISSIONS.CAN_RESOLVE_TASK_ISSUE,

  // Obligation Actions
  CREATE_OBLIGATION: PERMISSIONS.CAN_MANAGE_CONTRACT_OBLIGATIONS,
  EDIT_OBLIGATION: PERMISSIONS.CAN_MANAGE_CONTRACT_OBLIGATIONS,
  DELETE_OBLIGATION: PERMISSIONS.CAN_MANAGE_CONTRACT_OBLIGATIONS,

  // Financial Actions
  CREATE_FINANCIAL: PERMISSIONS.CAN_MANAGE_CONTRACT_FINANCIALS,
  EDIT_FINANCIAL: PERMISSIONS.CAN_MANAGE_CONTRACT_FINANCIALS,
  DELETE_FINANCIAL: PERMISSIONS.CAN_MANAGE_CONTRACT_FINANCIALS,

  // Negative Covenant Actions
  CREATE_NEGATIVE_COVENANT: PERMISSIONS.CAN_MANAGE_NEGATIVE_COVENANTS,
  EDIT_NEGATIVE_COVENANT: PERMISSIONS.CAN_MANAGE_NEGATIVE_COVENANTS,
  DELETE_NEGATIVE_COVENANT: PERMISSIONS.CAN_MANAGE_NEGATIVE_COVENANTS,

  // Timeline Extension Actions
  REQUEST_EXTENSION: PERMISSIONS.CAN_REQUEST_TIMELINE_EXTENSION,
  APPROVE_EXTENSION: PERMISSIONS.CAN_APPROVE_TIMELINE_EXTENSION,

  // Document Actions
  UPLOAD_DOCUMENT: PERMISSIONS.CAN_MANAGE_CONTRACT_DOCUMENTS,
  DELETE_DOCUMENT: PERMISSIONS.CAN_MANAGE_CONTRACT_DOCUMENTS,
};

// ─────────────────────────────────────────
// These Permissions are for Org-User to check what permission they have for a particular contract
// CONTRACT LEVEL PERMISSIONS
// Uses PERMISSIONS constants above
// ─────────────────────────────────────────
export const CONTRACT_LEVEL_PERMISSION = {
  signed_contract: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_SIGNED_CONTRACT,
    download: CONTRACT_PERMISSIONS.CAN_DOWNLOAD_SIGNED_CONTRACT,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_SIGNED_CONTRACT,
  },
  repository: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_REPOSITORY,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_REPOSITORY,
    download: CONTRACT_PERMISSIONS.CAN_DOWNLOAD_REPOSITORY,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_REPOSITORY,
  },
  responsibility: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_RESPONSIBILITY,
  },
  obligations: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_OBLIGATIONS,
    manage: CONTRACT_PERMISSIONS.CAN_MANAGE_OBLIGATIONS,
  },
  project_schedule: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_PROJECT_SCHEDULE,
    manage: CONTRACT_PERMISSIONS.CAN_MANAGE_PROJECT_SCHEDULE,
  },
  finance: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_FINANCE,
    manage_payment: CONTRACT_PERMISSIONS.CAN_MANAGE_PAYMENT_FINANCE,
  },
  approvals: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_APPROVALS,
    manage: CONTRACT_PERMISSIONS.CAN_MANAGE_APPROVALS,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_APPROVALS,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_APPROVALS,
    update: CONTRACT_PERMISSIONS.CAN_UPDATE_APPROVALS,
    manage: CONTRACT_PERMISSIONS.CAN_MANAGE_SUSPENSION_EVENT_UPDATE,
  },
  negativeCovenant: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_NEGATIVE_COVENANTS,
  },
  suspensionEvents: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_SUSPENSION_EVENTS,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_SUSPENSION_EVENTS,
    update: CONTRACT_PERMISSIONS.CAN_UPDATE_SUSPENSION_EVENTS,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_SUSPENSION_EVENTS,
    download: CONTRACT_PERMISSIONS.CAN_DOWNLOAD_SUSPENSION_EVENTS,
    manage: CONTRACT_PERMISSIONS.CAN_MANAGE_SUSPENSION_EVENT_UPDATE,
  },
  projectTimeline: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_PROJECT_TIMELINE,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_PROJECT_TIMELINE,
    update: CONTRACT_PERMISSIONS.CAN_EDIT_PROJECT_TIMELINE,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_PROJECT_TIMELINE,
  },
  reportingMeeting: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_REPORTING_MEETING,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_REPORTING_MEETING,
    update: CONTRACT_PERMISSIONS.CAN_EDIT_REPORTING_MEETING,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_REPORTING_MEETING,
  },
  deviations: {
    view: CONTRACT_PERMISSIONS.CAN_VIEW_DEVIATIONS,
    create: CONTRACT_PERMISSIONS.CAN_CREATE_DEVIATIONS,
    update: CONTRACT_PERMISSIONS.CAN_EDIT_DEVIATIONS,
    delete: CONTRACT_PERMISSIONS.CAN_DELETE_DEVIATIONS,
  },
};

export default PERMISSIONS;
